import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { formatDateWithSuffix } from "../../common/DateHelpers";

const DeliveryInfoPopover = ({
  projectSummary,
  isDeliveryInfoOpen,
  targetRef,
}) => {
  if (!targetRef?.current) return null;

  const panelCountDetails = projectSummary?.panel_count_details || {};

  const formatFeatureName = (name) => {
    return name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

    const renderShipmentTable = (shipmentData, shipmentKey) => {
    const tableRows = [];
    const shipmentKeys = Object.keys(panelCountDetails);
    const isFirstShipment = shipmentKeys[0] === shipmentKey;

    Object.entries(shipmentData).forEach(([category, categoryData]) => {
      if (
        typeof categoryData === "object" &&
        Object.keys(categoryData).length > 0
      ) {
        if (isFirstShipment) {
          // First shipment: Show only main categories with total_panels > 0
          if (categoryData.total_panels !== undefined && categoryData.total_panels > 0) {
            const panelText =
              categoryData.total_panels === 1 ? "panel" : "panels";
            tableRows.push(
              <tr key={`${shipmentKey}-${category}`}>
                <td className="text-start px-2 text-muted">
                  {formatFeatureName(category)}
                </td>
                <td className="text-end px-2 text-muted">
                  {categoryData.total_panels} {panelText}
                </td>
              </tr>
            );
          }
        } else {
          // Second shipment: Show only special features

          // For second shipment, check ALL categories for special features (even those with total_panels)
          Object.entries(categoryData).forEach(([feature, count]) => {
            // Skip the total_panels property itself
            if (feature === 'total_panels') return;

            const specialFeatures = ['cnc', 'pattern', 'finger_groove', 'g_profile', 'profile_shutter'];
            if (specialFeatures.includes(feature) && count > 0) {
              const panelText = count === 1 ? "panel" : "panels";
              tableRows.push(
                <tr key={`${shipmentKey}-${category}-${feature}`}>
                  <td className="text-start px-2 text-muted">
                    {formatFeatureName(feature)}
                  </td>
                  <td className="text-end px-2 text-muted">
                    {count} {panelText}
                  </td>
                </tr>
              );
            }
          });
        }
      }
    });

    // Add hardwares only to first shipment
    if (isFirstShipment) {
      tableRows.push(
        <tr key={`${shipmentKey}-hardwares`}>
          <td className="text-start px-2 text-muted">Hardwares</td>
          <td className="text-end px-2 text-muted">All</td>
        </tr>
      );
    }

    return tableRows;
  };

  const getPlannedDate = (date) => {
    return formatDateWithSuffix(date);
  };

  const shipmentKeys = Object.keys(panelCountDetails);
  const secondShipmentKey = shipmentKeys.length > 1 ? shipmentKeys[1] : null;

  const getSecondShipmentFeatures = () => {
    if (!secondShipmentKey) return [];

    const secondShipmentData = panelCountDetails[secondShipmentKey];
    const features = [];

    // Special features to always show for second shipment
    const specialFeatures = ['cnc', 'pattern', 'finger_groove', 'g_profile', 'profile_shutter'];

    Object.entries(secondShipmentData).forEach(([category, categoryData]) => {
      if (
        typeof categoryData === "object" &&
        Object.keys(categoryData).length > 0
      ) {
        // Check for nested features in all categories (including those with total_panels)
        if (categoryData.total_panels === undefined) {
          // For nested features, check if they are special features
          Object.entries(categoryData).forEach(([feature, count]) => {
            if (specialFeatures.includes(feature) && count > 0) {
              const featureText = `${formatFeatureName(feature)} - ${count} panel${count > 1 ? 's' : ''}`;
              features.push(featureText);
            }
          });
        } else {
          // For categories with total_panels, also check for nested features
          Object.entries(categoryData).forEach(([feature, count]) => {
            // Skip the total_panels property itself
            if (feature === 'total_panels') return;

            if (specialFeatures.includes(feature) && count > 0) {
              const featureText = `${formatFeatureName(feature)} - ${count} panel${count > 1 ? 's' : ''}`;
              features.push(featureText);
            }
          });
        }
      }
    });

    return features;
  };

  const secondShipmentFeatures = getSecondShipmentFeatures();

  return (
    isDeliveryInfoOpen && (
      <div
        className="position-absolute bg-white p-3 rounded-2"
        style={{
          right: "12%",
          top: "3.2rem",
          border: "1px solid #bbb",
          boxShadow: "5px 5px 10px #ccc",
          zIndex: 9,
          width: "25vw",
          fontSize: "13px",
        }}
      >
        {Object.entries(panelCountDetails).map(
          ([shipmentName, shipmentData]) => (
            <div key={shipmentName} className="small mb-4">
              <Container className="text-center p-0 mb-2">
                <div className="table-responsive">
                  <table className="table table-bordered align-middle text-center mb-0">
                    <thead>
                      <tr>
                        <th className="text-start px-2 table-secondary fw-bold">
                          {shipmentName}
                        </th>
                        <th className="table-secondary fw-bold text-end px-2">
                          {formatDateWithSuffix(shipmentData["planned_date"])}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderShipmentTable(shipmentData, shipmentName)}
                    </tbody>
                  </table>
                </div>
              </Container>
            </div>
          )
        )}

        <div className="mt-3 p-2 bg-light rounded text-muted">
          <p className="mb-2">
            Above dates are valid only if the order is placed today.
          </p>

          <p className="text-black mb-0 fw-bold">
            Please check the shipment dates once before placing the order.
          </p>
        </div>
      </div>
    )
  );
};

export default DeliveryInfoPopover;
